import { useUserContext } from '@vitvio/user-context';
import { dayjs, toZonedTime, type Dayjs } from '@vitvio/helpers';
import { createContext, useContext, useState, useEffect } from 'react';

interface TimeProviderContextType {
  currentTime: Dayjs;
  currentTimeInTimezone: Dayjs;
}

const TimeProviderContext = createContext<TimeProviderContextType>({
  currentTime: dayjs(),
  currentTimeInTimezone: dayjs(),
});

interface TimeProviderProps {
  children: React.ReactNode;
}

function TimeProvider({ children }: TimeProviderProps) {
  const [currentTime, setCurrentTime] = useState(dayjs());
  const { hospitalTimezone } = useUserContext();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs());
    }, 10000);

    // Cleanup function to clear the interval if the component unmounts
    return () => clearInterval(timer);
  }, []);

  const currentTimeInTimezone = toZonedTime(currentTime, hospitalTimezone);

  return (
    <TimeProviderContext.Provider
      value={{
        currentTime,
        currentTimeInTimezone: currentTimeInTimezone ?? dayjs(),
      }}
    >
      {children}
    </TimeProviderContext.Provider>
  );
}

function useTimeProviderContext() {
  const context = useContext(TimeProviderContext);
  if (context === undefined) throw new Error('Mission Control Calendar Context was used outside the provider.');
  return context;
}

export { useTimeProviderContext, TimeProvider };
